/**
 * 설치기사 현장 화면.
 *
 * Flutter WebView 셸 안에서 열리지만, 일반 모바일 브라우저에서도 그대로
 * 동작한다(@solar/native-bridge 가 모든 기능에 브라우저 폴백을 갖는다).
 * 레거시는 같은 화면이 Kotlin(android_engineer)과 Swift(ios)로 두 벌 있었고,
 * 사진 슬롯 개수마저 4장 vs 7장으로 서로 달랐다.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { historyState, navigate, useLocation, useSession } from '@solar/ui';
import { onResume } from '@solar/native-bridge';

import { api } from './api';
import { routeFromPath, routeToPath, TAB_PATH, type Route } from './routes';
import { AsList } from './screens/AsList';
import { DayPay } from './screens/DayPay';
import { Login } from './screens/Login';
import { OrderDetail } from './screens/OrderDetail';
import { OrderList } from './screens/OrderList';

const TAB_TITLE = { orders: '설치 목록', as: 'A/S 목록', daypay: '일일 수납' } as const;

export function App() {
  const session = useSession(api);
  // 화면은 주소가 정한다(routes.ts) — WebView 셸의 하드웨어 뒤로가기도 history 를 타므로
  // 상세에서 뒤로가기가 자연스럽게 목록으로 돌아간다.
  const location = useLocation();
  const route = useMemo<Route>(
    () => routeFromPath(location.path, historyState()),
    [location],
  );
  const setRoute = useCallback(
    (next: Route) =>
      navigate(routeToPath(next), next.name === 'order' ? { state: { from: next.from } } : {}),
    [],
  );
  const [refreshKey, setRefreshKey] = useState(0);

  // 모르는 경로는 설치 목록 주소로 정리한다.
  useEffect(() => {
    const canonical = routeToPath(route);
    if (location.path !== canonical) navigate(canonical, { replace: true });
  }, [location.path, route]);

  // 셸이 백그라운드에서 돌아오면 목록을 새로 읽는다 (배정이 바뀌었을 수 있다).
  useEffect(() => onResume(() => setRefreshKey((k) => k + 1)), []);

  // refresh 토큰까지 만료되면 로그인 화면으로 되돌린다.
  useEffect(() => {
    const handler = () => void session.logout();
    window.addEventListener('solar:unauthorized', handler);
    return () => window.removeEventListener('solar:unauthorized', handler);
  }, [session]);

  // 상세는 열었던 탭을 history state 에 기억해 두고 뒤로가기 시 그 탭으로 돌아간다.
  const openOrder = useCallback(
    (id: number) =>
      setRoute({ name: 'order', id, from: route.name === 'order' ? route.from : route.name }),
    [route, setRoute],
  );
  const back = useCallback(
    () => navigate(TAB_PATH[route.name === 'order' ? route.from : 'orders']),
    [route],
  );

  if (session.restoring) {
    return <p className="spinner">세션 확인 중…</p>;
  }

  if (!session.user) {
    return <Login onLogin={session.login} />;
  }

  if (route.name === 'order') {
    return <OrderDetail orderId={route.id} onBack={back} />;
  }

  return (
    <div className="shell">
      <header className="shell__head">
        <h1>{TAB_TITLE[route.name]}</h1>
        <button type="button" className="link" onClick={() => void session.logout()}>
          로그아웃
        </button>
      </header>

      <main className="shell__body">
        {route.name === 'orders' && <OrderList key={`orders-${refreshKey}`} onOpen={openOrder} />}
        {route.name === 'as' && (
          <AsList key={`as-${refreshKey}`} meId={session.user.id} onOpenOrder={openOrder} />
        )}
        {route.name === 'daypay' && (
          <DayPay key={`daypay-${refreshKey}`} onOpenOrder={openOrder} />
        )}
      </main>

      <nav className="tabs">
        <button
          type="button"
          className={route.name === 'orders' ? 'active' : ''}
          onClick={() => setRoute({ name: 'orders' })}
        >
          설치
        </button>
        <button
          type="button"
          className={route.name === 'as' ? 'active' : ''}
          onClick={() => setRoute({ name: 'as' })}
        >
          A/S
        </button>
        {/* iOS MainViewController 3번째 섹션(DayPayView) 파리티 */}
        <button
          type="button"
          className={route.name === 'daypay' ? 'active' : ''}
          onClick={() => setRoute({ name: 'daypay' })}
        >
          수납
        </button>
      </nav>
    </div>
  );
}
